import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowsOutSimple,
  CaretDown,
  Columns,
  Cube,
  DownloadSimple,
  ShareNetwork,
  Swatches,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { EDGE_LINK, EDGE_PROFILES, EDGE_TYPES, edgeTypeLabel } from "@/lib/edgeProfiles";
import { EdgeScene } from "@/components/site/EdgeCorner3D";
import { StonePicker } from "@/components/site/StonePicker";

const FALLBACK = { key: "default", name: "Taj Mahal Quartzite", photo: "" };

// The default stone for the 3D edges: the website's uploaded photo, else a matching slab
// on the racks, else the drawn Taj Mahal look (photo = "").
export const useEdgeStone = () => {
  const [stone, setStone] = useState(FALLBACK);
  useEffect(() => {
    fetch(`${API}/public/edge-stone`)
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (d) =>
          d &&
          setStone({
            key: "default",
            name: d.name || FALLBACK.name,
            photo: d.photo ? `${API}${d.photo}` : "",
          }),
      )
      .catch(() => {});
  }, []);
  return stone;
};

const seg = (on, dark) =>
  dark
    ? `h-9 px-3 text-xs font-semibold transition-colors duration-200 ${on ? "bg-white text-[#1c1b19]" : "text-white/70 hover:text-white"}`
    : `h-8 px-3 text-xs font-semibold transition-colors duration-200 ${on ? "bg-[#1c1b19] text-white" : "text-[#1c1b19]/70 hover:text-[#1c1b19]"}`;

const FINISHES = [
  ["polished", "Polished"],
  ["honed", "Honed"],
];
const finishLabel = (f) => (f === "polished" ? "Polished" : "Honed");
const thickOf = (profile) => (profile.type === "laminated" ? "6 cm" : "3 cm");

export const FinishToggle = ({ finish, onChange, dark = false, id = "" }) => (
  <div
    className={`flex items-center gap-1 p-1 ${dark ? "bg-[#3a3835]" : "bg-white/92 backdrop-blur"}`}
    data-testid={`edge-3d-finish${id}`}
  >
    {FINISHES.map(([f, label]) => (
      <button
        key={f}
        type="button"
        data-testid={`edge-3d-${f}${id}`}
        aria-pressed={finish === f}
        onClick={() => onChange(f)}
        className={seg(finish === f, dark)}
      >
        {label}
      </button>
    ))}
  </div>
);

const caption = (stone, finish, source, thick) =>
  [stone.name, thick, finishLabel(finish), source === "drawn" ? "rendered look" : null]
    .filter(Boolean)
    .join(" · ");

// Quote link carries the edge, plus the slab when the shopper picked one off the racks.
const quoteLink = (profile, stone) =>
  EDGE_LINK(profile.name, stone.key === "default" ? "" : stone.name);

// Inside the quick-view dialog: the scene with a light finish toggle, a stone button
// (opens the full viewer with the picker) and an expand button.
export const Edge3DInline = ({ profile, stone, onExpand }) => {
  const [finish, setFinish] = useState("polished");
  const [source, setSource] = useState("");
  return (
    <div className="relative h-full w-full" data-testid={`edge-3d-inline-${profile.slug}`}>
      <EdgeScene
        profile={profile}
        finish={finish}
        photo={stone.photo}
        onSource={setSource}
        testId="edge-3d-inline-canvas"
      />
      <div className="pointer-events-none absolute inset-x-3 top-3 flex items-start justify-between gap-2">
        <div className="pointer-events-auto">
          <FinishToggle finish={finish} onChange={setFinish} id="-inline" />
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          <button
            type="button"
            data-testid="edge-3d-inline-stone"
            onClick={() => onExpand(true)}
            className="flex h-10 max-w-[180px] items-center gap-1.5 bg-white/92 px-3 text-xs font-semibold text-[#1c1b19] backdrop-blur transition-colors duration-200 hover:bg-[#1c1b19] hover:text-white"
            title="Pick a different stone"
          >
            <Swatches size={14} weight="bold" className="shrink-0" />
            <span className="truncate">{stone.name}</span>
            <CaretDown size={11} weight="bold" className="shrink-0" />
          </button>
          <button
            type="button"
            data-testid="edge-3d-expand"
            onClick={() => onExpand(false)}
            aria-label="Open full screen"
            className="flex h-10 w-10 items-center justify-center bg-white/92 text-[#1c1b19] backdrop-blur transition-colors duration-200 hover:bg-[#1c1b19] hover:text-white"
          >
            <ArrowsOutSimple size={16} weight="bold" />
          </button>
        </div>
      </div>
      <div
        className="pointer-events-none absolute bottom-3 left-3 bg-white/92 px-3 py-1.5 text-[11px] font-medium text-[#1c1b19] backdrop-blur"
        data-testid="edge-3d-inline-caption"
      >
        {caption(stone, finish, source)}
      </div>
    </div>
  );
};

const snapshot = (api, profile, stone) => {
  const url = api.current?.();
  if (!url) return;
  const a = document.createElement("a");
  a.href = url;
  a.download = `${profile.slug}-${stone.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-3d.png`;
  a.click();
};

// Profile dropdown for a compare pane, grouped Single / Laminated
const ProfileSelect = ({ value, onChange, testId }) => (
  <label className="flex h-11 items-center gap-2 bg-white px-3 text-sm font-semibold text-[#1c1b19] shadow-lg ring-1 ring-black/10">
    <svg viewBox="0 22 200 100" className="h-5 w-9 shrink-0 text-[color:var(--s-gold)]" aria-hidden="true">
      <path d={value.path} fill="currentColor" />
    </svg>
    <span className="hidden text-[9px] font-bold uppercase tracking-[0.18em] text-[color:var(--s-mute)] sm:block">
      Edge
    </span>
    <select
      data-testid={testId}
      value={value.slug}
      onChange={(e) => onChange(EDGE_PROFILES.find((p) => p.slug === e.target.value))}
      className="h-full max-w-[190px] cursor-pointer appearance-none bg-transparent pr-1 outline-none"
    >
      {EDGE_TYPES.slice(1).map(([key, label]) => (
        <optgroup key={key} label={label}>
          {EDGE_PROFILES.filter((p) => p.type === key).map((p) => (
            <option key={p.slug} value={p.slug}>
              {p.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
    <CaretDown size={13} weight="bold" className="pointer-events-none -ml-1 shrink-0 text-[color:var(--s-gold)]" />
  </label>
);

// A different profile to start the comparison with: the next one of the same type.
const partnerOf = (profile) => {
  const same = EDGE_PROFILES.filter((p) => p.type === profile.type);
  return same[(same.findIndex((p) => p.slug === profile.slug) + 1) % same.length] || profile;
};

// Full-screen viewer opened from the gold 3D button on every card. `stone` is the one on
// the top right now; `fallback` is the site default; `stones` the racks for the picker.
// Compare splits the stage into two synced panes, same stone and finish.
export const Edge3DModal = ({
  profile,
  stone,
  fallback,
  stones,
  onStone,
  onClose,
  pickerOpen = false,
  initialFinish = "polished",
  initialB = null,
  initialCompare = false,
  shareLink = null,
}) => {
  const [a, setA] = useState(profile);
  const [b, setB] = useState(() => initialB || partnerOf(profile));
  const [compare, setCompare] = useState(initialCompare);
  const [finish, setFinish] = useState(initialFinish);
  const [source, setSource] = useState("");
  const [picker, setPicker] = useState(pickerOpen);
  const capture = useRef(null);
  const shared = useRef({ driver: null, pos: [0, 0, 0], target: [0, 0, 0] }).current;
  const syncA = useMemo(() => (compare ? { id: "a", shared } : undefined), [compare, shared]);
  const syncB = useMemo(() => (compare ? { id: "b", shared } : undefined), [compare, shared]);

  useEffect(() => {
    const key = (e) => {
      if (e.key !== "Escape") return;
      if (picker) setPicker(false);
      else onClose();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose, picker]);

  const pick = (s) => {
    onStone(s);
    setPicker(false);
  };
  const toggleCompare = () => {
    shared.driver = null;
    shared.auto = undefined;
    setCompare((c) => !c);
  };
  // Share a link that reopens this edge (and stone, and the compare partner) exactly as shown
  const share = async () => {
    const url = shareLink?.(a, stone, compare ? b : null);
    if (!url) return;
    const text = `${a.name} edge in ${stone.name}`;
    if (navigator.share) {
      try {
        await navigator.share({ url, title: text, text });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — it reopens this edge and stone");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const pane = (p, setP, sync, id) => (
    <div className="relative min-h-0 overflow-hidden bg-[#f3f1ec]" data-testid={`edge-3d-pane-${id}`}>
      <EdgeScene
        profile={p}
        finish={finish}
        photo={stone.photo}
        onSource={setSource}
        testId={`edge-3d-canvas-${id}`}
        sync={sync}
      />
      <div className="absolute left-3 top-3">
        <ProfileSelect value={p} onChange={setP} testId={`edge-3d-compare-select-${id}`} />
      </div>
      <div
        className="pointer-events-none absolute bottom-3 left-3 bg-white/92 px-3 py-1.5 text-[11px] font-medium text-[#1c1b19] backdrop-blur"
        data-testid={`edge-3d-pane-caption-${id}`}
      >
        {p.name} · {thickOf(p)} · {edgeTypeLabel(p.type).split(" · ")[0]}
      </div>
    </div>
  );

  return (
    <div
      className="site site-overlay fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-8"
      data-testid="edge-3d-modal"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${a.name} edge in 3D`}
    >
      <div
        className="flex max-h-full w-full max-w-[94vw] flex-col overflow-hidden bg-white"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <div className="flex flex-wrap items-center gap-3 bg-[#1c1b19] px-5 py-3 text-white">
          <span className="flex items-center gap-2 text-sm font-medium" data-testid="edge-3d-title">
            <Cube size={16} weight="bold" />
            {compare
              ? `Compare edges · ${stone.name} · ${finishLabel(finish)}`
              : `${a.name} · 3D · ${edgeTypeLabel(a.type)}`}
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="edge-3d-stone-btn"
              aria-expanded={picker}
              onClick={() => setPicker((p) => !p)}
              className={`flex h-9 max-w-[240px] items-center gap-2 px-3 text-xs font-semibold transition-colors duration-200 ${
                picker ? "bg-white text-[#1c1b19]" : "bg-[#3a3835] hover:bg-[#4a4844]"
              }`}
            >
              <Swatches size={14} weight="bold" className="shrink-0" />
              <span className="truncate" data-testid="edge-3d-stone-name">
                {stone.name}
              </span>
              <CaretDown size={11} weight="bold" className="shrink-0" />
            </button>
            <FinishToggle finish={finish} onChange={setFinish} dark />
            <button
              type="button"
              data-testid="edge-3d-compare"
              aria-pressed={compare}
              onClick={toggleCompare}
              className={`flex h-9 items-center gap-2 px-3 text-xs font-semibold transition-colors duration-200 ${
                compare ? "bg-white text-[#1c1b19]" : "bg-[#3a3835] hover:bg-[#4a4844]"
              }`}
            >
              <Columns size={14} weight="bold" /> Compare
            </button>
            {shareLink && (
              <button
                type="button"
                data-testid="edge-3d-share"
                onClick={share}
                className="flex h-9 items-center gap-2 bg-[#3a3835] px-3 text-xs font-semibold transition-colors duration-200 hover:bg-[#4a4844]"
              >
                <ShareNetwork size={14} weight="bold" /> Share
              </button>
            )}
            {!compare && (
              <button
                type="button"
                data-testid="edge-3d-snapshot"
                onClick={() => snapshot(capture, a, stone)}
                className="flex h-9 items-center gap-2 bg-[#3a3835] px-3 text-xs font-semibold transition-colors duration-200 hover:bg-[#4a4844]"
              >
                <DownloadSimple size={14} weight="bold" /> Snapshot
              </button>
            )}
            <Link
              to={quoteLink(a, stone)}
              data-testid="edge-3d-quote"
              className="flex h-9 items-center bg-[color:var(--s-gold-bright)] px-3 text-xs font-semibold text-[#1c1b19] transition-colors duration-200 hover:bg-white"
            >
              Quote this edge
            </Link>
            <button
              type="button"
              data-testid="edge-3d-close"
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center text-white/70 transition-colors duration-200 hover:bg-white/10 hover:text-white"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
        </div>
        <div className="relative h-[78vh] overflow-hidden bg-[#f3f1ec]">
          {compare ? (
            <div
              className="grid h-full grid-cols-1 grid-rows-2 gap-px bg-white sm:grid-cols-2 sm:grid-rows-1"
              data-testid="edge-3d-compare-stage"
            >
              {pane(a, setA, syncA, "a")}
              {pane(b, setB, syncB, "b")}
            </div>
          ) : (
            <>
              <EdgeScene
                profile={a}
                finish={finish}
                photo={stone.photo}
                onSource={setSource}
                captureApi={capture}
                testId="edge-3d-canvas"
              />
              <div className="absolute left-4 top-4">
                <ProfileSelect value={a} onChange={setA} testId="edge-3d-select" />
              </div>
              <div
                className="pointer-events-none absolute bottom-4 left-4 bg-white/92 px-3.5 py-2 text-xs font-medium text-[#1c1b19] backdrop-blur"
                data-testid="edge-3d-caption"
              >
                {caption(stone, finish, source, thickOf(a))}
              </div>
            </>
          )}
          <div className="pointer-events-none absolute bottom-4 right-4 hidden bg-white/80 px-3 py-1.5 text-[11px] text-[color:var(--s-mute)] backdrop-blur sm:block">
            {compare
              ? "Both panes turn together · drag either one"
              : "Drag to orbit · scroll to zoom · it turns on its own until you grab it"}
          </div>
          <StonePicker
            open={picker}
            onClose={() => setPicker(false)}
            stones={stones}
            current={stone}
            fallback={fallback}
            onPick={pick}
          />
        </div>
      </div>
    </div>
  );
};
